import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGym } from '../contexts/GymContext';
import { contaBancariaAPI, movimentacaoCaixaAPI, importacaoOFXAPI, expenseAPI } from '../services/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { RegrasOFXManager } from '../components/RegrasOFXManager';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  TrendingUp, 
  TrendingDown, 
  Wallet,
  Building2,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw,
  Filter,
  Upload,
  FileText,
  Check,
  X,
  AlertCircle,
  History,
  Wand2,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const CLASSIFICACOES_ENTRADA = [
  { value: 'recebimento_operacional', label: 'Recebimento de Cliente', description: 'Pagamento de cliente/venda', icon: TrendingUp, color: 'text-green-500' },
  { value: 'aporte_capital', label: 'Aporte / Injeção de Dinheiro', description: 'Dinheiro que você colocou na empresa', icon: Wallet, color: 'text-blue-500' },
  { value: 'emprestimo_recebido', label: 'Empréstimo Recebido', description: 'Dinheiro emprestado pelo banco', icon: Building2, color: 'text-purple-500' },
  { value: 'transferencia_entrada', label: 'Transferência entre Contas', description: 'Veio de outra conta sua', icon: RefreshCw, color: 'text-gray-500' },
  { value: 'outras_entradas', label: 'Outras Entradas', description: 'Estornos, devoluções, etc', icon: ArrowUpCircle, color: 'text-teal-500' },
];

const CLASSIFICACOES_SAIDA = [
  { value: 'operacional_recorrente', label: 'Custo Fixo / Operacional', description: 'Aluguel, luz, folha, impostos', icon: TrendingDown, color: 'text-red-500' },
  { value: 'retirada_recorrente', label: 'Retirada / Pró-labore', description: 'Seu salário ou distribuição', icon: Wallet, color: 'text-orange-500' },
  { value: 'pontual', label: 'Despesa Pontual', description: 'Gasto único, não se repete', icon: ArrowDownCircle, color: 'text-yellow-600' },
  { value: 'investimento', label: 'Investimento', description: 'Maquinário, obras, bens', icon: Building2, color: 'text-blue-500' },
  { value: 'parcela_compromisso', label: 'Parcela de Financiamento', description: 'Empréstimo, financiamento, leasing', icon: Building2, color: 'text-purple-500' },
  { value: 'transferencia_saida', label: 'Transferência entre Contas', description: 'Foi para outra conta sua', icon: RefreshCw, color: 'text-gray-500' },
  { value: 'outras_saidas', label: 'Outras Saídas', description: 'Taxas, ajustes, etc', icon: ArrowDownCircle, color: 'text-gray-600' },
];

const TIPO_DESPESA_LABEL = {
  operacional: 'Operacional',
  pontual: 'Pontual',
  investimento: 'Investimento',
};

// Tipos de despesa (passo 1 da classificação) com classes estáticas p/ o Tailwind
const TIPOS_DESPESA = [
  { value: 'operacional', label: 'Operacional', active: 'bg-emerald-500/20 border-emerald-500 text-emerald-400' },
  { value: 'pontual', label: 'Pontual', active: 'bg-amber-500/20 border-amber-500 text-amber-400' },
  { value: 'investimento', label: 'Investimento', active: 'bg-blue-500/20 border-blue-500 text-blue-400' },
];
const tipoDaCategoria = (c) => (c?.tipo_despesa) || (c?.is_operacional !== false ? 'operacional' : 'investimento');

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

export default function CaixaPage() {
  const { selectedGym } = useGym();
  const [contas, setContas] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [consolidado, setConsolidado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('movimentacoes');
  
  // Filtros
  const [filtroMes, setFiltroMes] = useState(MESES[new Date().getMonth()]);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroConta, setFiltroConta] = useState('todas');
  
  // Modais
  const [showContaModal, setShowContaModal] = useState(false);
  const [showMovModal, setShowMovModal] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [editingMov, setEditingMov] = useState(null);
  
  // Formulário Conta
  const [contaForm, setContaForm] = useState({
    nome: '',
    banco: '',
    agencia: '',
    conta: '',
    tipo: 'corrente',
    saldo_atual: 0,
    cor: '#A8C94A'
  });
  
  // Formulário Movimentação
  const [movForm, setMovForm] = useState({
    tipo: 'entrada',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    descricao: '',
    classificacao_entrada: 'recebimento_operacional',
    classificacao_saida: 'operacional_recorrente',
    categoria: '',
    tipo_despesa: '',
    conta_bancaria_id: '',
    is_transferencia_interna: false,
    observacao: ''
  });

  // Importação OFX
  const fileInputRef = useRef(null);
  const [showOFXModal, setShowOFXModal] = useState(false);
  const [ofxPreview, setOfxPreview] = useState(null);
  const [ofxLoading, setOfxLoading] = useState(false);
  const [ofxContaSelecionada, setOfxContaSelecionada] = useState('');
  const [ofxTransacoesSelecionadas, setOfxTransacoesSelecionadas] = useState([]);
  const [ofxAtualizarSaldo, setOfxAtualizarSaldo] = useState(true);
  const [showHistoricoOFX, setShowHistoricoOFX] = useState(false);
  const [showRegrasOFX, setShowRegrasOFX] = useState(false);
  const [historicoOFX, setHistoricoOFX] = useState([]);
  // Novos: seletor de mês antes do upload + abas/filtros no preview
  const [ofxEtapa, setOfxEtapa] = useState('escolher-mes'); // 'escolher-mes' | 'preview'
  const [ofxMesRef, setOfxMesRef] = useState('');
  const [ofxAnoRef, setOfxAnoRef] = useState(new Date().getFullYear());
  const [ofxArquivoPendente, setOfxArquivoPendente] = useState(null);
  const [ofxAbaAtiva, setOfxAbaAtiva] = useState('entradas'); // 'entradas' | 'saidas'
  const [ofxOrdem, setOfxOrdem] = useState('data_desc'); // 'data_desc' | 'data_asc' | 'valor_desc' | 'valor_asc'
  const [ofxTransacoesForaMes, setOfxTransacoesForaMes] = useState(0); // conta fora do mês
  const [expenseCategories, setExpenseCategories] = useState([]); // categorias de despesa configuradas
  const [ofxCategorias, setOfxCategorias] = useState({}); // { hash_dedup: nomeCategoria } para saídas
  const [ofxTipos, setOfxTipos] = useState({}); // { hash_dedup: tipo } passo 1 da classificação de saída

  // Lançamentos sem conta vinculada
  const [movsSemConta, setMovsSemConta] = useState([]);
  const [semContaFiltroTipo, setSemContaFiltroTipo] = useState('todos');
  const [semContaFiltroMes, setSemContaFiltroMes] = useState('todos');
  const [semContaAlvo, setSemContaAlvo] = useState(''); // conta para vincular
  const [semContaSelecionados, setSemContaSelecionados] = useState([]);
  const [semContaLoading, setSemContaLoading] = useState(false);
  const [semContaAtualizarSaldo, setSemContaAtualizarSaldo] = useState(true);

  // Recalcular saldo de conta
  const [showRecalcModal, setShowRecalcModal] = useState(false);
  const [recalcConta, setRecalcConta] = useState(null);
  const [recalcSaldoInicial, setRecalcSaldoInicial] = useState('0');

  const loadData = useCallback(async () => {
    if (!selectedGym?.id) return;
    setLoading(true);
    try {
      const [contasRes, movsRes, consolidadoRes, catsRes] = await Promise.all([
        contaBancariaAPI.getAll(selectedGym.id),
        movimentacaoCaixaAPI.getAll(selectedGym.id, { 
          mes: filtroMes, 
          ano: filtroAno,
          ...(filtroTipo !== 'todos' && { tipo: filtroTipo }),
          ...(filtroConta !== 'todas' && { conta_bancaria_id: filtroConta })
        }),
        movimentacaoCaixaAPI.getConsolidado(selectedGym.id, filtroMes, filtroAno),
        expenseAPI.getCategories(selectedGym.id)
      ]);
      setContas(contasRes.data);
      setMovimentacoes(movsRes.data);
      setConsolidado(consolidadoRes.data);
      setExpenseCategories(catsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados do caixa');
    } finally {
      setLoading(false);
    }
  }, [selectedGym?.id, filtroMes, filtroAno, filtroTipo, filtroConta]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lançamentos sem conta
  const loadSemConta = useCallback(async () => {
    if (!selectedGym?.id) return;
    setSemContaLoading(true);
    try {
      const res = await movimentacaoCaixaAPI.getSemConta(selectedGym.id, {
        ...(semContaFiltroTipo !== 'todos' && { tipo: semContaFiltroTipo }),
        ...(semContaFiltroMes !== 'todos' && { mes: semContaFiltroMes }),
      });
      setMovsSemConta(res.data || []);
      setSemContaSelecionados([]);
    } catch (error) {
      toast.error('Erro ao carregar lançamentos sem conta');
    } finally {
      setSemContaLoading(false);
    }
  }, [selectedGym?.id, semContaFiltroTipo, semContaFiltroMes]);

  useEffect(() => {
    if (activeTab === 'sem-conta') loadSemConta();
  }, [activeTab, loadSemConta]);

  const toggleSemContaSelecionado = (id) => {
    setSemContaSelecionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelecionarTodosSemConta = () => {
    if (semContaSelecionados.length === movsSemConta.length) {
      setSemContaSelecionados([]);
    } else {
      setSemContaSelecionados(movsSemConta.map((m) => m.id));
    }
  };

  const handleVincularConta = async () => {
    if (!semContaAlvo) {
      toast.error('Selecione a conta que deseja vincular');
      return;
    }
    if (semContaSelecionados.length === 0) {
      toast.error('Selecione ao menos um lançamento');
      return;
    }
    try {
      const res = await movimentacaoCaixaAPI.vincularConta({
        gym_id: selectedGym.id,
        conta_bancaria_id: semContaAlvo,
        movimentacao_ids: semContaSelecionados,
        atualizar_saldo: semContaAtualizarSaldo,
      });
      const msgSaldo = semContaAtualizarSaldo && res.data.saldo_atualizado != null
        ? ` — saldo atualizado para ${formatCurrency(res.data.saldo_atualizado)}`
        : '';
      toast.success(`${res.data.vinculadas} lançamento(s) vinculado(s) à conta ${res.data.conta}${msgSaldo}`);
      setSemContaAlvo('');
      await loadSemConta();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular conta');
    }
  };

  const handleCorrigirTransferencias = async () => {
    if (!window.confirm('Corrigir retroativamente os lançamentos já marcados como transferência entre contas? Isso NÃO altera o saldo, apenas tira essas transferências da receita/despesa (DRE e Fluxo).')) return;
    try {
      const res = await movimentacaoCaixaAPI.corrigirTransferencias({ gym_id: selectedGym.id });
      if (res.data.corrigidas > 0) {
        toast.success(`${res.data.corrigidas} transferência(s) corrigida(s); ${res.data.dre_removidas} despesa(s) removida(s) do DRE`);
      } else {
        toast.info('Nenhuma transferência pendente de correção encontrada');
      }
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao corrigir transferências');
    }
  };

  const handleForcarLeitura = async (conta) => {
    if (!conta) return;
    try {
      const res = await contaBancariaAPI.recalcularSaldo(conta.id, {
        gym_id: selectedGym.id,
        // saldo_inicial omitido de propósito: backend usa o saldo de abertura já salvo na conta
      });
      toast.success(`Saldo atualizado: ${formatCurrency(res.data.saldo_atual)} (${res.data.total_lancamentos} lançamentos)`);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao recalcular saldo');
    }
  };

  const handleRecalcularSaldo = async () => {
    if (!recalcConta) return;
    try {
      const res = await contaBancariaAPI.recalcularSaldo(recalcConta.id, {
        gym_id: selectedGym.id,
        saldo_inicial: parseFloat(recalcSaldoInicial) || 0,
      });
      toast.success(`Saldo recalculado: ${formatCurrency(res.data.saldo_atual)} (${res.data.total_lancamentos} lançamentos)`);
      setShowRecalcModal(false);
      setRecalcConta(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao recalcular saldo');
    }
  };

  // Handlers de Conta Bancária
  const handleSaveConta = async () => {
    try {
      if (editingConta) {
        await contaBancariaAPI.update(editingConta.id, contaForm);
        toast.success('Conta atualizada com sucesso');
      } else {
        await contaBancariaAPI.create({ ...contaForm, gym_id: selectedGym.id });
        toast.success('Conta criada com sucesso');
      }
      setShowContaModal(false);
      setEditingConta(null);
      resetContaForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar conta');
    }
  };

  const handleDeleteConta = async (id) => {
    if (!window.confirm('Excluir esta conta bancária?')) return;
    try {
      await contaBancariaAPI.delete(id);
      toast.success('Conta excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  const handleEditConta = (conta) => {
    setEditingConta(conta);
    setContaForm({
      nome: conta.nome,
      banco: conta.banco || '',
      agencia: conta.agencia || '',
      conta: conta.conta || '',
      tipo: conta.tipo || 'corrente',
      saldo_atual: conta.saldo_atual || 0,
      cor: conta.cor || '#A8C94A'
    });
    setShowContaModal(true);
  };

  const resetContaForm = () => {
    setContaForm({
      nome: '',
      banco: '',
      agencia: '',
      conta: '',
      tipo: 'corrente',
      saldo_atual: 0,
      cor: '#A8C94A'
    });
  };

  // Handlers de Movimentação
  const handleSaveMov = async () => {
    if (!movForm.valor || !movForm.descricao) {
      toast.error('Preencha valor e descrição');
      return;
    }
    
    try {
      const ehTransfSaida = movForm.tipo === 'saida' && movForm.tipo_despesa === 'transferencia';
      const payload = {
        gym_id: selectedGym.id,
        tipo: movForm.tipo,
        valor: parseFloat(movForm.valor),
        data: movForm.data,
        descricao: movForm.descricao,
        categoria: ehTransfSaida ? null : (movForm.categoria || null),
        conta_bancaria_id: movForm.conta_bancaria_id || null,
        observacao: movForm.observacao || null,
        is_transferencia_interna: movForm.tipo === 'entrada' 
          ? movForm.classificacao_entrada === 'transferencia_entrada'
          : ehTransfSaida
      };
      
      if (movForm.tipo === 'entrada') {
        payload.classificacao_entrada = movForm.classificacao_entrada;
      } else {
        const classifMap = { operacional: 'operacional_recorrente', pontual: 'pontual', investimento: 'investimento' };
        payload.classificacao_saida = ehTransfSaida
          ? 'transferencia_saida'
          : (classifMap[movForm.tipo_despesa] || movForm.classificacao_saida);
      }
      
      if (editingMov) {
        await movimentacaoCaixaAPI.update(editingMov.id, payload);
        toast.success('Movimentação atualizada');
      } else {
        await movimentacaoCaixaAPI.create(payload);
        toast.success('Movimentação criada');
      }
      
      setShowMovModal(false);
      setEditingMov(null);
      resetMovForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar movimentação');
    }
  };

  const handleDeleteMov = async (id) => {
    if (!window.confirm('Excluir esta movimentação?')) return;
    try {
      await movimentacaoCaixaAPI.delete(id);
      toast.success('Movimentação excluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleEditMov = (mov) => {
    setEditingMov(mov);
    setMovForm({
      tipo: mov.tipo,
      valor: mov.valor.toString(),
      data: mov.data,
      descricao: mov.descricao,
      classificacao_entrada: mov.classificacao_entrada || 'recebimento_operacional',
      classificacao_saida: mov.classificacao_saida || 'operacional_recorrente',
      categoria: mov.categoria || '',
      tipo_despesa: (() => {
        if (mov.tipo === 'saida' && mov.is_transferencia_interna) return 'transferencia';
        const c = expenseCategories.find(ec => ec.name === mov.categoria);
        if (c) return tipoDaCategoria(c);
        const cs = mov.classificacao_saida;
        if (cs === 'transferencia_saida') return 'transferencia';
        if (cs === 'operacional_recorrente') return 'operacional';
        if (cs === 'investimento') return 'investimento';
        if (cs === 'pontual' || cs === 'parcela_compromisso') return 'pontual';
        return '';
      })(),
      conta_bancaria_id: mov.conta_bancaria_id || '',
      is_transferencia_interna: mov.is_transferencia_interna || false,
      observacao: mov.observacao || ''
    });
    setShowMovModal(true);
  };

  const resetMovForm = () => {
    setMovForm({
      tipo: 'entrada',
      valor: '',
      data: new Date().toISOString().split('T')[0],
      descricao: '',
      classificacao_entrada: 'recebimento_operacional',
      classificacao_saida: 'operacional_recorrente',
      categoria: '',
      tipo_despesa: '',
      conta_bancaria_id: '',
      is_transferencia_interna: false,
      observacao: ''
    });
  };

  const getClassificacaoLabel = (mov) => {
    if (mov.tipo === 'entrada') {
      const classif = CLASSIFICACOES_ENTRADA.find(c => c.value === mov.classificacao_entrada);
      return classif?.label || mov.classificacao_entrada;
    } else {
      const classif = CLASSIFICACOES_SAIDA.find(c => c.value === mov.classificacao_saida);
      return classif?.label || mov.classificacao_saida;
    }
  };

  const getContaNome = (contaId) => {
    const conta = contas.find(c => c.id === contaId);
    return conta?.nome || '-';
  };

  // ============ HANDLERS OFX ============
  const MESES_BR = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];

  // Etapa 1: cliente clica em Importar OFX → abre modal na etapa 'escolher-mes'
  const abrirModalOFX = () => {
    setOfxEtapa('escolher-mes');
    setOfxPreview(null);
    setOfxTransacoesSelecionadas([]);
    setOfxArquivoPendente(null);
    setOfxMesRef(MESES_BR[new Date().getMonth()]);
    setOfxAnoRef(new Date().getFullYear());
    setOfxTransacoesForaMes(0);
    setShowOFXModal(true);
  };

  const handleOFXFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'ofx') {
      toast.error('Arquivo deve ser .OFX');
      return;
    }
    if (!ofxMesRef) {
      toast.error('Selecione o mês de referência antes');
      return;
    }
    setOfxArquivoPendente(file);
    setOfxLoading(true);
    try {
      const response = await importacaoOFXAPI.preview(selectedGym.id, file);
      // 1) Descartar BB Rende Fácil completamente (auto_ignorar já vem do backend)
      // 2) Filtrar transações fora do mês/ano de referência escolhido pelo cliente
      const todas = response.data.transacoes.filter(t => !t.auto_ignorar);
      const doMes = todas.filter(t => t.mes === ofxMesRef && t.ano === ofxAnoRef);
      const foraDoMes = todas.length - doMes.length;
      // Recalcular resumo apenas com transações válidas
      const novas = doMes.filter(t => !t.is_duplicada);
      const totalEntradas = novas.filter(t => t.tipo === 'entrada').reduce((s, t) => s + t.valor, 0);
      const totalSaidas = novas.filter(t => t.tipo === 'saida').reduce((s, t) => s + t.valor, 0);
      const dupCount = doMes.filter(t => t.is_duplicada).length;
      const aprendCount = doMes.filter(t => t.classificacao_fonte === 'aprendido').length;
      setOfxPreview({
        ...response.data,
        transacoes: doMes,
        resumo: {
          total_transacoes: doMes.length,
          novas: novas.length,
          duplicadas: dupCount,
          aprendidas: aprendCount,
          total_entradas: totalEntradas,
          total_saidas: totalSaidas
        }
      });
      setOfxTransacoesForaMes(foraDoMes);
      setOfxTransacoesSelecionadas(novas.map(t => t.hash_dedup));
      setOfxEtapa('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo OFX');
    } finally {
      setOfxLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleToggleTransacao = (hash) => {
    setOfxTransacoesSelecionadas(prev => 
      prev.includes(hash) 
        ? prev.filter(h => h !== hash)
        : [...prev, hash]
    );
  };

  const handleSelecionarTodas = () => {
    if (!ofxPreview) return;
    const todas = ofxPreview.transacoes
      .filter(t => !t.is_duplicada && !t.auto_ignorar)
      .map(t => t.hash_dedup);
    setOfxTransacoesSelecionadas(todas);
  };

  const handleDeselecionarTodas = () => {
    setOfxTransacoesSelecionadas([]);
  };

  const handleConfirmarImportacaoOFX = async () => {
    if (!ofxContaSelecionada || ofxContaSelecionada === 'none') {
      toast.error('Selecione a conta bancária do extrato antes de importar');
      return;
    }
    if (ofxTransacoesSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma transação');
      return;
    }
    
    setOfxLoading(true);
    try {
      const transacoesParaImportar = ofxPreview.transacoes
        .filter(t => ofxTransacoesSelecionadas.includes(t.hash_dedup))
        .map(t => {
          const ehTransf = Boolean(t.is_transferencia_interna);
          let classificacao;
          if (t.tipo === 'entrada') {
            classificacao = t.sugestao_classificacao || 'outras_entradas';
          } else {
            classificacao = ehTransf ? 'transferencia_saida' : (t.sugestao_classificacao || 'outras_saidas');
          }
          return {
            data: t.data,
            valor: t.valor,
            tipo: t.tipo,
            descricao: t.descricao,
            hash_dedup: t.hash_dedup,
            classificacao,
            categoria: (t.tipo === 'saida' && !ehTransf) ? (ofxCategorias[t.hash_dedup] || null) : null,
            is_transferencia_interna: ehTransf
          };
        });

      const contaId = (ofxContaSelecionada && ofxContaSelecionada !== 'none') ? ofxContaSelecionada : null;
      const deveAtualizarSaldo = Boolean(ofxAtualizarSaldo && contaId);

      const response = await importacaoOFXAPI.confirmar({
        gym_id: selectedGym.id,
        conta_bancaria_id: contaId,
        transacoes: transacoesParaImportar,
        atualizar_saldo_conta: deveAtualizarSaldo,
        saldo_final_ofx: deveAtualizarSaldo ? ofxPreview.saldo_final : null
      });
      
      toast.success(`${response.data.importadas} transações importadas com sucesso!`);
      setShowOFXModal(false);
      setOfxPreview(null);
      setOfxTransacoesSelecionadas([]);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao importar transações');
    } finally {
      setOfxLoading(false);
    }
  };

  const handleCarregarHistoricoOFX = async () => {
    try {
      const response = await importacaoOFXAPI.listar(selectedGym.id);
      setHistoricoOFX(response.data);
      setShowHistoricoOFX(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleExcluirLoteOFX = async (loteId) => {
    if (!window.confirm('Excluir todas as transações deste lote?')) return;
    try {
      await importacaoOFXAPI.excluir(loteId);
      toast.success('Lote excluído');
      handleCarregarHistoricoOFX();
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir lote');
    }
  };

  const alterarClassificacaoOFX = (hash, novaClassificacao) => {
    const ehTransferencia = novaClassificacao === 'transferencia_entrada' || novaClassificacao === 'transferencia_saida';
    setOfxPreview(prev => ({
      ...prev,
      transacoes: prev.transacoes.map(t => 
        t.hash_dedup === hash 
          ? { ...t, sugestao_classificacao: novaClassificacao, is_transferencia_interna: ehTransferencia }
          : t
      )
    }));
  };

  // Marca/desmarca uma SAÍDA do OFX como transferência entre contas
  const marcarTransferenciaSaidaOFX = (hash, ehTransf) => {
    setOfxPreview(prev => ({
      ...prev,
      transacoes: prev.transacoes.map(t =>
        t.hash_dedup === hash
          ? { ...t, is_transferencia_interna: ehTransf, sugestao_classificacao: ehTransf ? 'transferencia_saida' : t.sugestao_classificacao }
          : t
      )
    }));
    if (ehTransf) {
      setOfxCategorias(prev => ({ ...prev, [hash]: '' }));
    }
  };

  if (!selectedGym) {
    return (
      <DashboardLayout>
        <div>
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-8 text-center">
              <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" style={{ color: 'var(--color-primary)' }} />
              <p style={{ color: 'var(--app-text-secondary)' }}>Selecione uma empresa para ver o caixa</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--app-text-primary)' }}>
            Caixa
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--app-text-secondary)' }}>
            Movimentações reais do banco — entradas e saídas efetivas
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Botão Importar OFX */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".ofx"
            onChange={handleOFXFileSelect}
            className="hidden"
          />
          <Button 
            variant="outline" 
            className="gap-2" 
            style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
            onClick={abrirModalOFX}
            data-testid="btn-importar-ofx"
          >
            <Upload size={18} />
            <span className="hidden sm:inline">Importar OFX</span>
          </Button>

          {/* Histórico OFX */}
          <Button 
            variant="outline" 
            className="gap-2" 
            style={{ borderColor: 'var(--app-border)' }}
            onClick={handleCarregarHistoricoOFX}
          >
            <History size={18} />
            <span className="hidden sm:inline">Histórico</span>
          </Button>

          {/* Corrigir transferências antigas (retroativo) */}
          <Button
            variant="outline"
            className="gap-2"
            style={{ borderColor: 'var(--app-border)' }}
            onClick={handleCorrigirTransferencias}
            data-testid="btn-corrigir-transferencias"
            title="Marca lançamentos antigos já classificados como transferência para não contarem como receita/despesa"
          >
            <RefreshCw size={18} />
            <span className="hidden sm:inline">Corrigir transferências</span>
          </Button>

          <Dialog open={showContaModal} onOpenChange={(open) => {
            setShowContaModal(open);
            if (!open) { setEditingConta(null); resetContaForm(); }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" style={{ borderColor: 'var(--app-border)' }}>
                <Building2 size={18} />
                <span className="hidden sm:inline">Contas</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
              <DialogHeader>
                <DialogTitle style={{ color: 'var(--app-text-primary)' }}>
                  {editingConta ? 'Editar Conta' : 'Nova Conta Bancária'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Nome da Conta *</Label>
                  <Input
                    value={contaForm.nome}
                    onChange={(e) => setContaForm({ ...contaForm, nome: e.target.value })}
                    placeholder="Ex: Itaú PJ, Nubank"
                    style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Banco</Label>
                    <Input
                      value={contaForm.banco}
                      onChange={(e) => setContaForm({ ...contaForm, banco: e.target.value })}
                      placeholder="Itaú"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    />
                  </div>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Tipo</Label>
                    <Select value={contaForm.tipo} onValueChange={(v) => setContaForm({ ...contaForm, tipo: v })}>
                      <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                        <SelectItem value="corrente">Corrente</SelectItem>
                        <SelectItem value="poupanca">Poupança</SelectItem>
                        <SelectItem value="investimento">Investimento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Agência</Label>
                    <Input
                      value={contaForm.agencia}
                      onChange={(e) => setContaForm({ ...contaForm, agencia: e.target.value })}
                      placeholder="0001"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    />
                  </div>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Conta</Label>
                    <Input
                      value={contaForm.conta}
                      onChange={(e) => setContaForm({ ...contaForm, conta: e.target.value })}
                      placeholder="12345-6"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    />
                  </div>
                </div>
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Saldo Atual (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={contaForm.saldo_atual}
                    onChange={(e) => setContaForm({ ...contaForm, saldo_atual: parseFloat(e.target.value) || 0 })}
                    style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => { setShowContaModal(false); setEditingConta(null); resetContaForm(); }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveConta} style={{ backgroundColor: 'var(--color-primary)' }}>
                    {editingConta ? 'Salvar' : 'Criar Conta'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Modal Recalcular Saldo */}
          <Dialog open={showRecalcModal} onOpenChange={(open) => { setShowRecalcModal(open); if (!open) setRecalcConta(null); }}>
            <DialogContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
              <DialogHeader>
                <DialogTitle style={{ color: 'var(--app-text-primary)' }}>
                  Recalcular saldo{recalcConta ? ` — ${recalcConta.nome}` : ''}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                  O saldo será recalculado como: <strong>saldo de abertura + soma de todos os lançamentos vinculados a esta conta</strong>
                  {' '}(entradas somam, saídas subtraem). Use isto para corrigir contas cujos lançamentos não somaram no saldo.
                </p>
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Saldo de abertura (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={recalcSaldoInicial}
                    onChange={(e) => setRecalcSaldoInicial(e.target.value)}
                    data-testid="recalc-saldo-inicial-input"
                    style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                    Quanto a conta tinha ANTES dos lançamentos. Se a conta começou zerada, deixe 0.
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setShowRecalcModal(false); setRecalcConta(null); }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleRecalcularSaldo} data-testid="recalc-saldo-confirmar" style={{ backgroundColor: 'var(--color-primary)' }}>
                    Recalcular
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          
          <Dialog open={showMovModal} onOpenChange={(open) => {
            setShowMovModal(open);
            if (!open) { setEditingMov(null); resetMovForm(); }
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2" style={{ backgroundColor: 'var(--color-primary)' }}>
                <Plus size={18} />
                <span className="hidden sm:inline">Movimentação</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
              <DialogHeader>
                <DialogTitle style={{ color: 'var(--app-text-primary)' }}>
                  {editingMov ? 'Editar Movimentação' : 'Nova Movimentação'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Tipo */}
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Tipo *</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant={movForm.tipo === 'entrada' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => setMovForm({ ...movForm, tipo: 'entrada' })}
                      style={movForm.tipo === 'entrada' ? { backgroundColor: '#22c55e' } : {}}
                    >
                      <ArrowUpCircle size={18} />
                      Entrada
                    </Button>
                    <Button
                      type="button"
                      variant={movForm.tipo === 'saida' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => setMovForm({ ...movForm, tipo: 'saida' })}
                      style={movForm.tipo === 'saida' ? { backgroundColor: '#ef4444' } : {}}
                    >
                      <ArrowDownCircle size={18} />
                      Saída
                    </Button>
                  </div>
                </div>
                
                {/* Classificação — apenas ENTRADAS. Saídas são classificadas pela categoria (tipo) abaixo, igual ao OFX */}
                {movForm.tipo === 'entrada' && (
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Classificação *</Label>
                  <Select 
                    value={movForm.tipo === 'entrada' ? movForm.classificacao_entrada : movForm.classificacao_saida}
                    onValueChange={(v) => {
                      if (movForm.tipo === 'entrada') {
                        setMovForm({ ...movForm, classificacao_entrada: v });
                      } else {
                        setMovForm({ ...movForm, classificacao_saida: v });
                      }
                    }}
                  >
                    <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                      {(movForm.tipo === 'entrada' ? CLASSIFICACOES_ENTRADA : CLASSIFICACOES_SAIDA).map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          <div className="flex items-center gap-2">
                            <c.icon size={16} className={c.color} />
                            <span>{c.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                    {(movForm.tipo === 'entrada' ? CLASSIFICACOES_ENTRADA : CLASSIFICACOES_SAIDA)
                      .find(c => c.value === (movForm.tipo === 'entrada' ? movForm.classificacao_entrada : movForm.classificacao_saida))?.description}
                  </p>
                </div>
                )}
                
                {/* Valor e Data */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Valor (R$) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={movForm.valor}
                      onChange={(e) => setMovForm({ ...movForm, valor: e.target.value })}
                      placeholder="0,00"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    />
                  </div>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Data *</Label>
                    <Input
                      type="date"
                      value={movForm.data}
                      onChange={(e) => setMovForm({ ...movForm, data: e.target.value })}
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    />
                  </div>
                </div>
                
                {/* Descrição */}
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Descrição *</Label>
                  <Input
                    value={movForm.descricao}
                    onChange={(e) => setMovForm({ ...movForm, descricao: e.target.value })}
                    placeholder="Ex: Pagamento cliente João, Aluguel dezembro"
                    style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                  />
                </div>
                
                {/* Conta Bancária */}
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Conta Bancária</Label>
                  <Select 
                    value={movForm.conta_bancaria_id || 'none'}
                    onValueChange={(v) => setMovForm({ ...movForm, conta_bancaria_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                      <SelectValue placeholder="Selecione uma conta" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                      <SelectItem value="none">Nenhuma específica</SelectItem>
                      {contas.map((conta) => (
                        <SelectItem key={conta.id} value={conta.id}>
                          {conta.nome} ({formatCurrency(conta.saldo_atual)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Categoria e Observação */}
                <div className="space-y-4">
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>{movForm.tipo === 'saida' ? 'Classificação (Categoria) *' : 'Categoria'}</Label>
                    {movForm.tipo === 'saida' ? (
                      <div className="space-y-2">
                        <div className="flex gap-2 flex-wrap">
                          {TIPOS_DESPESA.map((t) => {
                            const sel = movForm.tipo_despesa === t.value;
                            return (
                              <button
                                key={t.value}
                                type="button"
                                onClick={() => setMovForm({ ...movForm, tipo_despesa: t.value, categoria: '' })}
                                data-testid={`mov-tipo-${t.value}`}
                                className={`flex-1 min-w-0 px-2 py-1.5 rounded text-xs font-medium transition-all border text-center whitespace-nowrap ${sel ? t.active : 'bg-transparent'}`}
                                style={sel ? {} : { borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
                              >
                                {t.label}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => setMovForm({ ...movForm, tipo_despesa: 'transferencia', categoria: '' })}
                            data-testid="mov-tipo-transferencia"
                            className={`flex-1 min-w-0 px-2 py-1.5 rounded text-xs font-medium transition-all border text-center whitespace-nowrap ${movForm.tipo_despesa === 'transferencia' ? 'bg-gray-500/20 border-gray-400 text-gray-300' : 'bg-transparent'}`}
                            style={movForm.tipo_despesa === 'transferencia' ? {} : { borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
                          >
                            Transferência
                          </button>
                        </div>
                        {movForm.tipo_despesa === 'transferencia' ? (
                          <p className="text-xs" style={{ color: 'var(--app-text-muted)' }} data-testid="mov-transf-info">
                            Transferência entre contas — sai do saldo desta conta, mas não entra como despesa no DRE nem no Fluxo.
                          </p>
                        ) : movForm.tipo_despesa && (() => {
                          const cats = expenseCategories.filter((c) => tipoDaCategoria(c) === movForm.tipo_despesa);
                          return (
                            <Select
                              value={movForm.categoria || 'none'}
                              onValueChange={(v) => setMovForm({ ...movForm, categoria: v === 'none' ? '' : v })}
                            >
                              <SelectTrigger data-testid="mov-categoria-select" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                                <SelectValue placeholder="Escolha a categoria..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-[260px]" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                                <SelectItem value="none">Sem categoria</SelectItem>
                                {cats.length === 0 ? (
                                  <div className="px-3 py-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Nenhuma categoria "{TIPO_DESPESA_LABEL[movForm.tipo_despesa]}" — cadastre em Configurações</div>
                                ) : cats.map((c) => (
                                  <SelectItem key={c.id} value={c.name}>
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: c.color || '#EF4444' }} />
                                      {c.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </div>
                    ) : (
                      <Input
                        value={movForm.categoria}
                        onChange={(e) => setMovForm({ ...movForm, categoria: e.target.value })}
                        placeholder="Ex: Recebimento cliente"
                        style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                      />
                    )}
                    {movForm.tipo === 'saida' && (
                      <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                        Escolha o tipo e depois a categoria. Operacional entra no DRE do mês; Pontual/Investimento ficam só no Fluxo.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label style={{ color: 'var(--app-text-secondary)' }}>Observação</Label>
                    <Input
                      value={movForm.observacao}
                      onChange={(e) => setMovForm({ ...movForm, observacao: e.target.value })}
                      placeholder="Opcional"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => { setShowMovModal(false); setEditingMov(null); resetMovForm(); }}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSaveMov} style={{ backgroundColor: 'var(--color-primary)' }}>
                    {editingMov ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Cards de Resumo */}
      {consolidado && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wallet size={18} style={{ color: 'var(--color-primary)' }} />
                <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Saldo Total</span>
              </div>
              <p className="text-xl md:text-2xl font-bold" data-testid="caixa-saldo-total" style={{ color: (consolidado.saldo_total_contas ?? 0) < 0 ? '#ef4444' : 'var(--color-primary)' }}>
                {formatCurrency(consolidado.saldo_total_contas)}
              </p>
            </CardContent>
          </Card>
          
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpCircle size={18} className="text-green-500" />
                <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Entradas</span>
              </div>
              <p className="text-xl md:text-2xl font-bold text-green-500">
                {formatCurrency(consolidado.entradas?.total)}
              </p>
            </CardContent>
          </Card>
          
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDownCircle size={18} className="text-red-500" />
                <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Saídas</span>
              </div>
              <p className="text-xl md:text-2xl font-bold text-red-500">
                {formatCurrency(consolidado.saidas?.total)}
              </p>
            </CardContent>
          </Card>
          
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} style={{ color: consolidado.saldo_periodo >= 0 ? '#22c55e' : '#ef4444' }} />
                <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Saldo Período</span>
              </div>
              <p className="text-xl md:text-2xl font-bold" style={{ color: consolidado.saldo_periodo >= 0 ? '#22c55e' : '#ef4444' }}>
                {formatCurrency(consolidado.saldo_periodo)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs: Movimentações e Contas */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4" style={{ backgroundColor: 'var(--app-card-bg)' }}>
          <TabsTrigger value="movimentacoes" data-testid="tab-movimentacoes">Movimentações</TabsTrigger>
          <TabsTrigger value="contas" data-testid="tab-contas">Contas Bancárias</TabsTrigger>
          <TabsTrigger value="sem-conta" data-testid="tab-sem-conta">
            Sem Conta
            {movsSemConta.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-500">
                {movsSemConta.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="movimentacoes">
          {/* Filtros */}
          <Card className="mb-4" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter size={16} style={{ color: 'var(--app-text-secondary)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--app-text-secondary)' }}>Filtros</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Select value={filtroMes} onValueChange={setFiltroMes}>
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    {MESES.map((m) => (
                      <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filtroAno.toString()} onValueChange={(v) => setFiltroAno(parseInt(v))}>
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    {[2023, 2024, 2025, 2026].map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="entrada">Entradas</SelectItem>
                    <SelectItem value="saida">Saídas</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filtroConta} onValueChange={setFiltroConta}>
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    <SelectItem value="todas">Todas as contas</SelectItem>
                    {contas.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Lista de Movimentações */}
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardHeader>
              <CardTitle style={{ color: 'var(--app-text-primary)' }}>
                Movimentações - {filtroMes.charAt(0).toUpperCase() + filtroMes.slice(1)}/{filtroAno}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }}>
                  Carregando...
                </div>
              ) : movimentacoes.length === 0 ? (
                <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }}>
                  Nenhuma movimentação encontrada
                </div>
              ) : (
                <div className="space-y-2">
                  {movimentacoes.map((mov) => (
                    <div
                      key={mov.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-full ${mov.tipo === 'entrada' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          {mov.tipo === 'entrada' ? (
                            <ArrowUpCircle size={18} className="text-green-500" />
                          ) : (
                            <ArrowDownCircle size={18} className="text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" style={{ color: 'var(--app-text-primary)' }}>
                            {mov.descricao}
                          </p>
                          <div className="flex flex-wrap gap-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                            <span>{formatDate(mov.data)}</span>
                            <span>•</span>
                            <span>{getClassificacaoLabel(mov)}</span>
                            {mov.conta_bancaria_id && (
                              <>
                                <span>•</span>
                                <span>{getContaNome(mov.conta_bancaria_id)}</span>
                              </>
                            )}
                            {mov.is_transferencia_interna && (
                              <>
                                <span>•</span>
                                <span className="text-gray-400">Transf. Interna</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={`font-bold whitespace-nowrap ${mov.tipo === 'entrada' ? 'text-green-500' : 'text-red-500'}`}>
                          {mov.tipo === 'entrada' ? '+' : '-'} {formatCurrency(mov.valor)}
                        </span>
                        <Button variant="ghost" size="icon" onClick={() => handleEditMov(mov)}>
                          <Edit2 size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteMov(mov.id)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="contas">
          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ color: 'var(--app-text-primary)' }}>Contas Bancárias</CardTitle>
              <Button
                size="sm"
                className="gap-2"
                onClick={() => { setEditingConta(null); resetContaForm(); setShowContaModal(true); }}
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <Plus size={16} />
                Nova Conta
              </Button>
            </CardHeader>
            <CardContent>
              {contas.length === 0 ? (
                <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }}>
                  Nenhuma conta cadastrada
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {contas.map((conta) => (
                    <Card key={conta.id} style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: conta.cor || '#A8C94A' }} />
                              <h3 className="font-medium" style={{ color: 'var(--app-text-primary)' }}>{conta.nome}</h3>
                            </div>
                            {conta.banco && (
                              <p className="text-sm mt-1" style={{ color: 'var(--app-text-muted)' }}>
                                {conta.banco} {conta.agencia && `• Ag: ${conta.agencia}`} {conta.conta && `• Cc: ${conta.conta}`}
                              </p>
                            )}
                            <p className="text-sm mt-1 capitalize" style={{ color: 'var(--app-text-secondary)' }}>
                              {conta.tipo}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditConta(conta)}>
                              <Edit2 size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteConta(conta.id)}>
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--app-border)' }}>
                          <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Saldo atual</span>
                          <p className="text-xl font-bold" style={{ color: conta.saldo_atual >= 0 ? 'var(--color-primary)' : '#ef4444' }}>
                            {formatCurrency(conta.saldo_atual)}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 w-full gap-2"
                            onClick={() => handleForcarLeitura(conta)}
                            data-testid={`recalcular-saldo-${conta.id}`}
                            title="Força o sistema a reler e somar todos os lançamentos vinculados a esta conta"
                          >
                            <RefreshCw size={14} />
                            Recalcular saldo
                          </Button>
                          <button
                            onClick={() => { setRecalcConta(conta); setRecalcSaldoInicial(String(conta.saldo_inicial ?? 0)); setShowRecalcModal(true); }}
                            className="mt-2 w-full text-xs underline decoration-dotted underline-offset-2"
                            style={{ color: 'var(--app-text-muted)' }}
                            data-testid={`ajustar-abertura-${conta.id}`}
                          >
                            Ajustar saldo de abertura
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sem-conta">
          <Card className="mb-4" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardContent className="p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle size={18} className="text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                  Estes lançamentos foram importados <strong>sem conta bancária vinculada</strong>. Eles continuam contando normalmente
                  no DRE, Fluxo e Projeção — ficam aqui apenas para você organizar e vincular a uma conta.
                  <strong> Nada é descartado.</strong> Ao vincular, você pode somar os valores ao saldo da conta (recomendado, já que eles nunca haviam entrado no saldo).
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select value={semContaFiltroTipo} onValueChange={setSemContaFiltroTipo}>
                  <SelectTrigger data-testid="sem-conta-filtro-tipo" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    <SelectItem value="todos">Todos os tipos</SelectItem>
                    <SelectItem value="entrada">Entradas</SelectItem>
                    <SelectItem value="saida">Saídas</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={semContaFiltroMes} onValueChange={setSemContaFiltroMes}>
                  <SelectTrigger data-testid="sem-conta-filtro-mes" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    <SelectItem value="todos">Todos os meses</SelectItem>
                    {MESES.map((m) => (
                      <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <CardHeader>
              <CardTitle style={{ color: 'var(--app-text-primary)' }}>
                Lançamentos sem conta {movsSemConta.length > 0 && `(${movsSemConta.length})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Barra de vinculação */}
              {movsSemConta.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 p-3 rounded-lg border" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelecionarTodosSemConta}
                    data-testid="sem-conta-selecionar-todos"
                  >
                    {semContaSelecionados.length === movsSemConta.length ? 'Limpar seleção' : 'Selecionar todos'}
                  </Button>
                  <div className="flex-1">
                    <Select value={semContaAlvo} onValueChange={setSemContaAlvo}>
                      <SelectTrigger data-testid="sem-conta-select-conta" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                        <SelectValue placeholder="Escolha a conta para vincular" />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                        {contas.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={handleVincularConta}
                    disabled={!semContaAlvo || semContaSelecionados.length === 0}
                    data-testid="sem-conta-vincular-btn"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  >
                    <Link2 size={16} />
                    Vincular conta ({semContaSelecionados.length})
                  </Button>
                </div>
              )}

              {movsSemConta.length > 0 && (
                <div className="flex items-center gap-2 mb-4 -mt-2 px-1">
                  <Checkbox
                    id="sem-conta-atualizar-saldo"
                    checked={semContaAtualizarSaldo}
                    onCheckedChange={setSemContaAtualizarSaldo}
                    data-testid="sem-conta-atualizar-saldo"
                  />
                  <label htmlFor="sem-conta-atualizar-saldo" className="text-xs cursor-pointer" style={{ color: 'var(--app-text-muted)' }}>
                    Somar estes lançamentos ao saldo da conta (recomendado, pois eles nunca haviam entrado no saldo)
                  </label>
                </div>
              )}

              {contas.length === 0 && movsSemConta.length > 0 && (
                <div className="mb-4 p-3 rounded-lg border text-sm flex items-center justify-between gap-3" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-secondary)' }}>
                  <span>Você ainda não tem contas cadastradas. Cadastre uma conta para poder vincular.</span>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('contas')} data-testid="sem-conta-ir-contas">
                    Ir para Contas
                  </Button>
                </div>
              )}

              {semContaLoading ? (
                <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }}>Carregando...</div>
              ) : movsSemConta.length === 0 ? (
                <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }} data-testid="sem-conta-vazio">
                  Nenhum lançamento sem conta. Tudo organizado! 🎉
                </div>
              ) : (
                <div className="space-y-2">
                  {movsSemConta.map((mov) => (
                    <div
                      key={mov.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                      style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}
                      data-testid={`sem-conta-item-${mov.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Checkbox
                          checked={semContaSelecionados.includes(mov.id)}
                          onCheckedChange={() => toggleSemContaSelecionado(mov.id)}
                          data-testid={`sem-conta-check-${mov.id}`}
                        />
                        <div className={`p-2 rounded-full ${mov.tipo === 'entrada' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          {mov.tipo === 'entrada' ? (
                            <ArrowUpCircle size={18} className="text-green-500" />
                          ) : (
                            <ArrowDownCircle size={18} className="text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" style={{ color: 'var(--app-text-primary)' }}>
                            {mov.descricao}
                          </p>
                          <div className="flex flex-wrap gap-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                            <span>{formatDate(mov.data)}</span>
                            <span>•</span>
                            <span className="capitalize">{mov.mes}/{mov.ano}</span>
                            <span>•</span>
                            <span>{getClassificacaoLabel(mov)}</span>
                          </div>
                        </div>
                      </div>
                      <span className={`font-bold whitespace-nowrap ml-2 ${mov.tipo === 'entrada' ? 'text-green-500' : 'text-red-500'}`}>
                        {mov.tipo === 'entrada' ? '+' : '-'} {formatCurrency(mov.valor)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Modal de Importação OFX */}
      <Dialog open={showOFXModal} onOpenChange={(open) => {
        if (!open) {
          setOfxPreview(null);
          setOfxTransacoesSelecionadas([]);
          setOfxContaSelecionada('');
          setOfxArquivoPendente(null);
          setOfxEtapa('escolher-mes');
        }
        setShowOFXModal(open);
      }}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }} data-testid="ofx-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg" style={{ color: 'var(--app-text-primary)' }}>
              <FileText size={22} style={{ color: 'var(--color-primary)' }} />
              Importar Extrato OFX
              {ofxEtapa === 'preview' && ofxMesRef && (
                <span className="text-sm font-normal ml-2 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}>
                  {ofxMesRef.charAt(0).toUpperCase() + ofxMesRef.slice(1)}/{ofxAnoRef}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* ETAPA 1: Escolher mês/ano + upload */}
          {ofxEtapa === 'escolher-mes' && (
            <div className="space-y-6 py-6" data-testid="ofx-etapa-mes">
              <div className="p-4 rounded-lg border-l-4" style={{ backgroundColor: 'var(--app-background)', borderLeftColor: 'var(--color-primary)' }}>
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--app-text-primary)' }}>Como funciona?</p>
                <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  1. Escolha o <strong>mês e ano</strong> do extrato. 2. Selecione a <strong>conta bancária</strong> do extrato (obrigatório). 3. Anexe o arquivo <code>.ofx</code>. O sistema descarta automaticamente aplicações internas (BB Rende Fácil, CDB automático) e transações fora do mês selecionado.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Mês de Referência *</Label>
                  <Select value={ofxMesRef} onValueChange={setOfxMesRef}>
                    <SelectTrigger className="mt-1" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }} data-testid="ofx-select-mes">
                      <SelectValue placeholder="Selecione o mês" />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                      {MESES_BR.map(m => (
                        <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label style={{ color: 'var(--app-text-secondary)' }}>Ano *</Label>
                  <Select value={String(ofxAnoRef)} onValueChange={(v) => setOfxAnoRef(parseInt(v))}>
                    <SelectTrigger className="mt-1" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }} data-testid="ofx-select-ano">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                      {[2024, 2025, 2026, 2027].map(a => (
                        <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Conta bancária — OBRIGATÓRIA para importar */}
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Conta Bancária *</Label>
                {contas.length === 0 ? (
                  <div className="mt-2 p-4 rounded-lg border-l-4 flex items-start justify-between gap-3" style={{ backgroundColor: 'var(--app-background)', borderLeftColor: '#f59e0b' }} data-testid="ofx-sem-conta-aviso">
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#f59e0b' }}>Cadastre uma conta bancária primeiro</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>Para importar o OFX você precisa vincular as movimentações a uma conta. Cadastre a conta do extrato e volte aqui para importar.</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => { setShowOFXModal(false); setEditingConta(null); resetContaForm(); setShowContaModal(true); }}
                      style={{ backgroundColor: 'var(--color-primary)', whiteSpace: 'nowrap' }}
                      data-testid="ofx-cadastrar-conta-btn"
                    >
                      Cadastrar Conta
                    </Button>
                  </div>
                ) : (
                  <>
                    <Select value={ofxContaSelecionada || ''} onValueChange={(v) => setOfxContaSelecionada(v)}>
                      <SelectTrigger className="mt-1" style={{ backgroundColor: 'var(--app-background)', borderColor: ofxContaSelecionada ? 'var(--app-border)' : '#f59e0b', color: 'var(--app-text-primary)' }} data-testid="ofx-select-conta">
                        <SelectValue placeholder="Selecione a conta do extrato..." />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                        {contas.map((conta) => (
                          <SelectItem key={conta.id} value={conta.id}>{conta.nome} ({formatCurrency(conta.saldo_atual)})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!ofxContaSelecionada && (
                      <p className="text-xs mt-1" style={{ color: '#f59e0b' }}>Selecione a conta do extrato para liberar o envio do arquivo.</p>
                    )}
                  </>
                )}
              </div>

              <div className="pt-4 border-t" style={{ borderColor: 'var(--app-border)' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".ofx"
                  onChange={handleOFXFileSelect}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!ofxMesRef || !ofxContaSelecionada || ofxLoading}
                  className="w-full py-6 text-base"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  data-testid="ofx-btn-anexar"
                >
                  {ofxLoading ? (
                    <><RefreshCw size={18} className="mr-2 animate-spin" /> Processando arquivo...</>
                  ) : (
                    <><Upload size={18} className="mr-2" /> Anexar arquivo .OFX</>
                  )}
                </Button>
              </div>

              {/* Regras de Categorização */}
              <div className="pt-4 border-t" style={{ borderColor: 'var(--app-border)' }}>
                <button
                  onClick={() => setShowRegrasOFX((v) => !v)}
                  className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity"
                  style={{ color: 'var(--color-primary)' }}
                  data-testid="ofx-toggle-regras"
                >
                  <Wand2 size={16} />
                  {showRegrasOFX ? 'Ocultar regras de categorização' : 'Gerenciar regras de categorização automática'}
                </button>
                {showRegrasOFX && (
                  <div className="mt-4">
                    <RegrasOFXManager gymId={selectedGym?.id} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ETAPA 2: Preview com abas e filtros */}
          {ofxEtapa === 'preview' && ofxPreview && (
            <div className="space-y-5 mt-2" data-testid="ofx-etapa-preview">
              {/* Resumo cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--app-background)' }}>
                  <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Total</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--app-text-primary)' }}>{ofxPreview.resumo.total_transacoes}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--app-background)' }}>
                  <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Novas</p>
                  <p className="text-lg font-bold text-green-500">{ofxPreview.resumo.novas}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--app-background)' }}>
                  <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Entradas</p>
                  <p className="text-lg font-bold text-green-500">{formatCurrency(ofxPreview.resumo.total_entradas)}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--app-background)' }}>
                  <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Saídas</p>
                  <p className="text-lg font-bold text-red-500">{formatCurrency(ofxPreview.resumo.total_saidas)}</p>
                </div>
              </div>

              {/* Avisos: fora do mês e Rende Fácil */}
              {ofxTransacoesForaMes > 0 && (
                <div className="p-3 rounded-lg border-l-4 border-yellow-500 flex items-start gap-2" style={{ backgroundColor: 'rgba(245,158,11,0.08)' }}>
                  <AlertCircle size={16} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs" style={{ color: 'var(--app-text-secondary)' }}>
                    <strong>{ofxTransacoesForaMes}</strong> transação(ões) descartada(s) por estar fora de <strong>{ofxMesRef}/{ofxAnoRef}</strong>. Importe outro mês separadamente.
                  </p>
                </div>
              )}

              {/* Atualização do saldo da conta — explicado para o cliente */}
              {(ofxContaSelecionada && ofxContaSelecionada !== 'none') && (
                <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--app-background)', borderColor: ofxAtualizarSaldo ? 'var(--color-primary)' : 'var(--app-border)' }} data-testid="ofx-atualizar-saldo-box">
                  {ofxPreview.saldo_final !== null && (
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Saldo final do extrato (OFX)</p>
                      <p className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>{formatCurrency(ofxPreview.saldo_final)}</p>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="atualizar-saldo"
                      checked={ofxAtualizarSaldo}
                      onCheckedChange={setOfxAtualizarSaldo}
                      className="mt-0.5"
                      data-testid="ofx-checkbox-atualizar-saldo"
                    />
                    <div>
                      <Label htmlFor="atualizar-saldo" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--app-text-primary)' }}>
                        Atualizar o saldo da conta com esta importação
                      </Label>
                      <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
                        {ofxAtualizarSaldo ? (
                          ofxPreview.saldo_final !== null
                            ? <>✅ <strong>MARCADO:</strong> o saldo da conta será ajustado para <strong>{formatCurrency(ofxPreview.saldo_final)}</strong> (saldo final do extrato).</>
                            : <>✅ <strong>MARCADO:</strong> o saldo da conta será somado/subtraído pelas movimentações importadas (o extrato não trouxe saldo final).</>
                        ) : (
                          <>⬜ <strong>DESMARCADO:</strong> as movimentações serão importadas, mas o <strong>saldo da conta NÃO muda</strong> agora. Você pode atualizar depois no card da conta em "Recalcular saldo".</>
                        )}
                      </p>
                      <p className="text-[11px] mt-1.5" style={{ color: 'var(--app-text-muted)', opacity: 0.8 }}>
                        Dica: se este OFX é o extrato mais recente e completo da conta, deixe <strong>MARCADO</strong>. Se você está importando um extrato antigo/parcial, deixe <strong>DESMARCADO</strong> para não bagunçar o saldo atual.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Vincular à conta */}
              <div>
                <Label style={{ color: 'var(--app-text-secondary)' }}>Vincular à conta bancária</Label>
                <Select value={ofxContaSelecionada || 'none'} onValueChange={(v) => setOfxContaSelecionada(v === 'none' ? '' : v)}>
                  <SelectTrigger style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)', color: 'var(--app-text-primary)' }}>
                    <SelectValue placeholder="Selecione uma conta" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    <SelectItem value="none">Nenhuma (importar sem vincular)</SelectItem>
                    {contas.map((conta) => (
                      <SelectItem key={conta.id} value={conta.id}>{conta.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtros e ordenação */}
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: 'var(--app-border)' }}>
                <Label className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Ordenar por:</Label>
                <Select value={ofxOrdem} onValueChange={setOfxOrdem}>
                  <SelectTrigger className="w-[220px] h-9" style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }} data-testid="ofx-select-ordem">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                    <SelectItem value="data_desc">Data (mais recente primeiro)</SelectItem>
                    <SelectItem value="data_asc">Data (mais antiga primeiro)</SelectItem>
                    <SelectItem value="valor_desc">Valor (maior primeiro)</SelectItem>
                    <SelectItem value="valor_asc">Valor (menor primeiro)</SelectItem>
                  </SelectContent>
                </Select>
                <span className="ml-auto text-sm" style={{ color: 'var(--app-text-muted)' }}>
                  <strong>{ofxTransacoesSelecionadas.length}</strong> selecionadas
                </span>
              </div>

              {/* Ações em massa */}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelecionarTodas}>
                  <Check size={14} className="mr-1" /> Selecionar novas
                </Button>
                <Button variant="outline" size="sm" onClick={handleDeselecionarTodas}>
                  <X size={14} className="mr-1" /> Limpar seleção
                </Button>
              </div>

              {/* Abas Entradas / Saídas */}
              <Tabs value={ofxAbaAtiva} onValueChange={setOfxAbaAtiva} className="w-full">
                <TabsList className="grid grid-cols-2 w-full" style={{ backgroundColor: 'var(--app-background)' }}>
                  <TabsTrigger value="entradas" data-testid="ofx-tab-entradas" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-500">
                    <ArrowUpCircle size={16} className="mr-2" />
                    Entradas ({ofxPreview.transacoes.filter(t => t.tipo === 'entrada').length})
                  </TabsTrigger>
                  <TabsTrigger value="saidas" data-testid="ofx-tab-saidas" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-500">
                    <ArrowDownCircle size={16} className="mr-2" />
                    Saídas ({ofxPreview.transacoes.filter(t => t.tipo === 'saida').length})
                  </TabsTrigger>
                </TabsList>

                {['entradas', 'saidas'].map(aba => (
                  <TabsContent key={aba} value={aba} className="mt-3">
                    {aba === 'saidas' && (
                      <p className="text-xs mb-2 px-1" style={{ color: 'var(--app-text-muted)' }} data-testid="ofx-saidas-hint">
                        Classifique cada saída escolhendo a <span style={{ color: 'var(--color-primary)' }}>categoria de despesa</span>. Categorias <b>Operacionais</b> entram automaticamente no DRE do mês; <b>Pontual</b> e <b>Investimento</b> aparecem apenas no Fluxo.
                      </p>
                    )}
                    <div className="max-h-[420px] overflow-y-auto space-y-2 border rounded-lg p-3" style={{ borderColor: 'var(--app-border)' }} data-testid={`ofx-lista-${aba}`}>
                      {(() => {
                        const filtro = aba === 'entradas' ? 'entrada' : 'saida';
                        let lista = ofxPreview.transacoes.filter(t => t.tipo === filtro);
                        if (ofxOrdem === 'data_desc') lista = [...lista].sort((a, b) => b.data.localeCompare(a.data));
                        else if (ofxOrdem === 'data_asc') lista = [...lista].sort((a, b) => a.data.localeCompare(b.data));
                        else if (ofxOrdem === 'valor_desc') lista = [...lista].sort((a, b) => b.valor - a.valor);
                        else if (ofxOrdem === 'valor_asc') lista = [...lista].sort((a, b) => a.valor - b.valor);
                        if (lista.length === 0) {
                          return <p className="text-center py-8 text-sm" style={{ color: 'var(--app-text-muted)' }}>Nenhuma {aba === 'entradas' ? 'entrada' : 'saída'} no arquivo.</p>;
                        }
                        return lista.map((trans) => (
                          <div
                            key={trans.hash_dedup}
                            className={`flex items-center gap-3 p-3 rounded-lg ${trans.is_duplicada ? 'opacity-50' : ''}`}
                            style={{ backgroundColor: 'var(--app-background)' }}
                          >
                            <Checkbox
                              checked={ofxTransacoesSelecionadas.includes(trans.hash_dedup)}
                              onCheckedChange={() => handleToggleTransacao(trans.hash_dedup)}
                              disabled={trans.is_duplicada}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate" style={{ color: 'var(--app-text-primary)' }}>{trans.descricao}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{formatDate(trans.data)}</p>
                                {trans.classificacao_fonte === 'aprendido' && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-500" title={`Confiança: ${trans.classificacao_confianca}%`}>IA</span>
                                )}
                                {trans.is_duplicada && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-500">Duplicada</span>
                                )}
                              </div>
                            </div>
                            {!trans.is_duplicada && trans.tipo === 'entrada' && (
                              <Select
                                value={trans.sugestao_classificacao}
                                onValueChange={(v) => alterarClassificacaoOFX(trans.hash_dedup, v)}
                              >
                                <SelectTrigger className="w-[200px] h-9 text-sm" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                                  {CLASSIFICACOES_ENTRADA.map((c) => (
                                    <SelectItem key={c.value} value={c.value} className="text-sm">{c.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {!trans.is_duplicada && trans.tipo === 'saida' && (
                              <div className="flex flex-col items-end gap-1" data-testid={`ofx-saida-${trans.hash_dedup}`}>
                                <div className="flex gap-1 flex-wrap justify-end">
                                  {TIPOS_DESPESA.map((t) => {
                                    const sel = ofxTipos[trans.hash_dedup] === t.value;
                                    return (
                                      <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => { setOfxTipos(prev => ({ ...prev, [trans.hash_dedup]: t.value })); setOfxCategorias(prev => ({ ...prev, [trans.hash_dedup]: '' })); marcarTransferenciaSaidaOFX(trans.hash_dedup, false); }}
                                        data-testid={`ofx-tipo-${t.value}-${trans.hash_dedup}`}
                                        className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all border ${sel ? t.active : 'bg-transparent'}`}
                                        style={sel ? {} : { borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
                                      >
                                        {t.label}
                                      </button>
                                    );
                                  })}
                                  {(() => {
                                    const sel = ofxTipos[trans.hash_dedup] === 'transferencia' || trans.is_transferencia_interna;
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => { setOfxTipos(prev => ({ ...prev, [trans.hash_dedup]: 'transferencia' })); marcarTransferenciaSaidaOFX(trans.hash_dedup, true); }}
                                        data-testid={`ofx-tipo-transferencia-${trans.hash_dedup}`}
                                        className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all border ${sel ? 'bg-gray-500/20 border-gray-400 text-gray-300' : 'bg-transparent'}`}
                                        style={sel ? {} : { borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
                                      >
                                        Transferência
                                      </button>
                                    );
                                  })()}
                                </div>
                                {(ofxTipos[trans.hash_dedup] === 'transferencia' || trans.is_transferencia_interna) ? (
                                  <span className="text-[11px] text-right max-w-[220px]" style={{ color: 'var(--app-text-muted)' }} data-testid={`ofx-transf-info-${trans.hash_dedup}`}>
                                    Transferência entre contas — sai do saldo desta conta, mas não entra como despesa no DRE nem no Fluxo.
                                  </span>
                                ) : ofxTipos[trans.hash_dedup] && (() => {
                                  const tipoSel = ofxTipos[trans.hash_dedup];
                                  const cats = expenseCategories.filter((c) => tipoDaCategoria(c) === tipoSel);
                                  return (
                                    <Select
                                      value={ofxCategorias[trans.hash_dedup] || ''}
                                      onValueChange={(v) => setOfxCategorias(prev => ({ ...prev, [trans.hash_dedup]: v }))}
                                    >
                                      <SelectTrigger
                                        className="w-[220px] h-9 text-sm"
                                        style={{ backgroundColor: 'var(--app-card-bg)', borderColor: ofxCategorias[trans.hash_dedup] ? 'var(--app-border)' : '#EF4444' }}
                                        data-testid={`ofx-categoria-select-${trans.hash_dedup}`}
                                      >
                                        <SelectValue placeholder="Escolha a categoria..." />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-[280px]" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
                                        {cats.length === 0 ? (
                                          <div className="px-3 py-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>Nenhuma categoria "{TIPO_DESPESA_LABEL[tipoSel]}" — cadastre em Configurações</div>
                                        ) : cats.map((c) => (
                                          <SelectItem key={c.id} value={c.name} className="text-sm">
                                            <span className="flex items-center gap-2">
                                              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: c.color || '#EF4444' }} />
                                              {c.name}
                                            </span>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                })()}
                              </div>
                            )}
                            <span className={`font-bold whitespace-nowrap text-base ${trans.tipo === 'entrada' ? 'text-green-500' : 'text-red-500'}`}>
                              {trans.tipo === 'entrada' ? '+' : '-'}{formatCurrency(trans.valor)}
                            </span>
                          </div>
                        ));
                      })()}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {ofxPreview.resumo.aprendidas > 0 && (
                <p className="text-xs text-center" style={{ color: 'var(--app-text-muted)' }}>
                  <span className="text-green-500">{ofxPreview.resumo.aprendidas}</span> classificações baseadas no seu histórico de importações
                </p>
              )}

              {/* Botões */}
              <div className="flex justify-end gap-2 pt-4 border-t" style={{ borderColor: 'var(--app-border)' }}>
                <Button variant="outline" onClick={() => { setOfxEtapa('escolher-mes'); setOfxPreview(null); }}>Voltar</Button>
                <Button
                  onClick={handleConfirmarImportacaoOFX}
                  disabled={ofxLoading || ofxTransacoesSelecionadas.length === 0}
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  data-testid="ofx-btn-confirmar"
                >
                  {ofxLoading ? (
                    <><RefreshCw size={16} className="mr-2 animate-spin" /> Importando...</>
                  ) : (
                    <><Upload size={16} className="mr-2" /> Importar {ofxTransacoesSelecionadas.length} transações</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Histórico de Importações */}
      <Dialog open={showHistoricoOFX} onOpenChange={setShowHistoricoOFX}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--app-text-primary)' }}>
              <History size={20} style={{ color: 'var(--color-primary)' }} />
              Histórico de Importações OFX
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {historicoOFX.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--app-text-secondary)' }}>
                Nenhuma importação realizada
              </div>
            ) : (
              historicoOFX.map((lote) => (
                <div
                  key={lote.lote_id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  style={{ backgroundColor: 'var(--app-background)', borderColor: 'var(--app-border)' }}
                >
                  <div>
                    <p className="font-medium" style={{ color: 'var(--app-text-primary)' }}>
                      {lote.total_transacoes} transações
                    </p>
                    <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                      Período: {lote.periodo}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                      Importado em: {new Date(lote.data_importacao).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-500">+{formatCurrency(lote.total_entradas)}</p>
                    <p className="text-sm text-red-500">-{formatCurrency(lote.total_saidas)}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1 text-red-500 hover:text-red-600"
                      onClick={() => handleExcluirLoteOFX(lote.lote_id)}
                    >
                      <Trash2 size={14} className="mr-1" /> Excluir lote
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
}
